import React from "react";
import { View, StyleSheet } from "react-native";
import { ColumnDef } from "@/components/NewTable";
import { ThemedText } from "@/components/ThemedText";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import Avatar from "@/components/Avatar";
import { theme } from "@/constants/themes";
import { ShiftUserMetaType } from "@/types/db/shift_users";

export type ShiftUserRow = {
  id: string;
  user_id: string;
  status: string;
  meta_data?: ShiftUserMetaType;
  user?: {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    preferred_name?: string | null;
    email_address?: string | null;
    meta_data?: {
      phones?: { type?: string; number?: string; sms_opted_in?: boolean }[];
    };
  };
  position_name?: string;
};

// Status color mapping matching RelatedUsersScreen pattern
const STATUS_COLORS: Record<string, keyof Pick<theme, "complete" | "pending" | "attention" | "neutral" | "todo">> = {
  CONFIRMED: "complete",
  ACCEPTED: "complete",
  INVITED: "pending",
  QUEUED: "neutral",
  DECLINED: "attention",
  CANCELLED: "attention",
  "NOT NEEDED": "neutral",
  "NO RESPONSE": "attention",
  "NO SHOW": "attention",
  "ATTENDANCE EXCEPTION": "attention",
  COMPLETE: "todo",
};

interface StatusPillProps {
  status: string;
  colors: theme;
}

export function StatusPill({ status, colors }: StatusPillProps) {
  const colorKey = STATUS_COLORS[status] || "neutral";
  const backgroundColor = colors[colorKey].border;

  return (
    <View style={[styles.statusPill, { backgroundColor }]}>
      <ThemedText style={styles.statusText}>{status}</ThemedText>
    </View>
  );
}

// Boolean icon component for table cells
interface BooleanIconProps {
  value: boolean;
  trueIcon: keyof typeof MaterialCommunityIcons.glyphMap;
  falseIcon?: keyof typeof MaterialCommunityIcons.glyphMap;
  colors: theme;
}

export function BooleanIcon({ value, trueIcon, falseIcon, colors }: BooleanIconProps) {
  if (!value && !falseIcon) {
    return <ThemedText style={styles.booleanEmpty}>—</ThemedText>;
  }

  return (
    <MaterialCommunityIcons
      name={value ? trueIcon : falseIcon!}
      size={16}
      color={value ? colors.complete.border : colors.textSecondary}
    />
  );
}

// Status options for editable picker (with colors)
const getStatusOptions = (colors: theme) => [
  { value: "QUEUED", label: "Queued", color: colors.neutral.border },
  { value: "INVITED", label: "Invited", color: colors.pending.border },
  { value: "ACCEPTED", label: "Accepted", color: colors.complete.border },
  { value: "DECLINED", label: "Declined", color: colors.attention.border },
  { value: "CANCELLED", label: "Cancelled", color: colors.attention.border },
  { value: "NOT NEEDED", label: "Not Needed", color: colors.neutral.border },
  { value: "NO RESPONSE", label: "No Response", color: colors.attention.border },
  { value: "NO SHOW", label: "No Show", color: colors.attention.border },
  { value: "ATTENDANCE EXCEPTION", label: "Attendance Exception", color: colors.attention.border },
  { value: "COMPLETE", label: "Complete", color: colors.todo.border },
];

export const getAssignedUsersColumns = (
  colors: theme,
  onUserPress?: (row: ShiftUserRow) => void,
): ColumnDef<ShiftUserRow>[] => [
  {
    key: "avatar",
    header: "",
    width: 48,
    align: "center",
    sortable: false,
    searchable: false,
    render: (_, row) => (
      <Avatar
        userId={row.user_id}
        email={row.user?.email_address ?? undefined}
        firstName={row.user?.preferred_name || row.user?.first_name || ""}
        lastName={row.user?.last_name || ""}
        size={30}
      />
    ),
  },
  {
    key: "name",
    header: "Name",
    flex: 1,
    minWidth: 150,
    accessor: (row) =>
      `${row.user?.preferred_name || row.user?.first_name || ""} ${row.user?.last_name || ""}`.trim() || "—",
    onPress: onUserPress,
  },
  {
    key: "opened",
    header: "Opened",
    width: 110,
    align: "center",
    searchable: false,
    accessor: (row) => (row.meta_data?.opened ? 1 : 0),
    render: (_, row) => <BooleanIcon value={row.meta_data?.opened ?? false} trueIcon="email-open" colors={colors} />,
  },
  {
    key: "viewed",
    header: "Viewed",
    width: 110,
    align: "center",
    searchable: false,
    accessor: (row) => (row.meta_data?.viewed ? 1 : 0),
    render: (_, row) => <BooleanIcon value={row.meta_data?.viewed ?? false} trueIcon="eye" colors={colors} />,
  },
  {
    key: "sms_sent",
    header: "SMS",
    width: 80,
    align: "center",
    searchable: false,
    accessor: (row) => (row.meta_data?.sms_sent ? 1 : 0),
    render: (_, row) => {
      const { sms_sent, sms_error } = row.meta_data ?? {};
      if (sms_sent) return <BooleanIcon value trueIcon="cellphone-message" colors={colors} />;
      if (sms_error?.includes("opted out"))
        return <MaterialCommunityIcons name="cellphone-off" size={16} color={colors.attention.border} />;
      if (sms_error)
        return <MaterialCommunityIcons name="cellphone-remove" size={16} color={colors.attention.border} />;
      return <ThemedText style={styles.booleanEmpty}>—</ThemedText>;
    },
    tooltip: (_, row) => {
      const { sms_sent, sms_error } = (row as ShiftUserRow).meta_data ?? {};
      if (sms_sent) return "SMS sent";
      if (sms_error) return sms_error;
      return "SMS not sent";
    },
  },
  {
    key: "sms_opt_in",
    header: "Opt-In",
    width: 80,
    align: "center",
    searchable: false,
    accessor: (row) => {
      const cell = row.user?.meta_data?.phones?.find((p) => p.type === "cell");
      return cell?.sms_opted_in === false ? 0 : cell ? 1 : -1;
    },
    render: (_, row) => {
      const cell = row.user?.meta_data?.phones?.find((p) => p.type === "cell");
      if (!cell) return <ThemedText style={styles.booleanEmpty}>—</ThemedText>;
      if (cell.sms_opted_in === false)
        return <MaterialCommunityIcons name="cellphone-off" size={16} color={colors.attention.border} />;
      return <MaterialCommunityIcons name="cellphone-check" size={16} color={colors.complete.border} />;
    },
    tooltip: (_, row) => {
      const cell = (row as ShiftUserRow).user?.meta_data?.phones?.find((p) => p.type === "cell");
      if (!cell) return "No cell phone on file";
      if (cell.sms_opted_in === false) return "User opted out of SMS";
      return "SMS opt-in";
    },
  },
  {
    key: "status",
    header: "Status",
    width: 130,
    align: "center",
    searchable: true,
    searchValue: (_, row) => row.status || "",
    accessor: (row) => row.status,
    render: (_, row) => <StatusPill status={row.status} colors={colors} />,
    editable: {
      type: "picker",
      options: getStatusOptions(colors),
      placeholder: "Select status",
    },
  },
];

const styles = StyleSheet.create({
  statusPill: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  statusText: {
    fontSize: 10,
    fontWeight: "600",
    color: "white",
  },
  booleanEmpty: {
    fontSize: 12,
    opacity: 0.5,
  },
});
